import React, { useState } from "react";
import CustomInput from "../../components/common/CustomInput/CustomInput";
import logo from "../../assets/images/delta-tieup-logo.png";
import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { loginUserAsync } from "../../redux/features/authSlice";
import CircularProgress from "@mui/material/CircularProgress";

const AUTH_FEATURES = [
   { icon: "fa-truck", label: "Fleet & trip management" },
   { icon: "fa-route", label: "Real-time logistics tracking" },
   { icon: "fa-shield-alt", label: "Secure role-based access" },
];

const Login = () => {
   const [loading, setLoading] = useState(false);
   const navigate = useNavigate();
   const dispatch = useDispatch();

   const loginSchema = yup.object().shape({
      mobileNo: yup.string().min(9, "Too Short").max(10, "Too long").required("Required"),
      password: yup.string().min(6, "Too Short").max(50, "Too Long").required("Required"),
   });

   const handleLogin = async (formData) => {
      setLoading(true);
      const data = {
         mobile: formData.mobileNo,
         password: formData.password,
      };
      dispatch(
         loginUserAsync({
            data,
            done: (response) => {
               setLoading(false);
               const roleId = response?.data?.user?.role_id;
               navigate(roleId === 5 ? "/trip/contractual/view" : "/dashboard");
            },
            err: () => {
               setLoading(false);
            },
         })
      );
   };

   const formik = useFormik({
      initialValues: {
         mobileNo: "",
         password: "",
      },
      validationSchema: loginSchema,
      onSubmit: handleLogin,
   });

   const { errors, getFieldProps, touched, handleSubmit } = formik;

   return (
      <div className="auth-page">
         <div className="auth-page__shell">
            <section className="auth-page__brand-panel" aria-label="Delta Tieup branding">
               <div className="auth-page__brand-content">
                  <img
                     src={logo}
                     alt="Delta Tieup"
                     className="app-brand-logo auth-page__brand-logo"
                  />
                  <h1 className="auth-page__brand-name">
                     DELTA <span>TIEUP</span>
                  </h1>
                  <p className="auth-page__brand-tagline">Delivering Trust</p>
                  <p className="auth-page__brand-copy">
                     Manage fleet operations, trips, and logistics workflows from one secure platform.
                  </p>
                  <ul className="auth-page__feature-list">
                     {AUTH_FEATURES.map((feature) => (
                        <li key={feature.label} className="auth-page__feature-item">
                           <span className="auth-page__feature-icon">
                              <i className={`fas ${feature.icon}`} />
                           </span>
                           <span>{feature.label}</span>
                        </li>
                     ))}
                  </ul>
               </div>
            </section>

            <section className="auth-page__form-panel">
               <div className="auth-page__mobile-brand">
                  <img
                     src={logo}
                     alt="Delta Tieup"
                     className="app-brand-logo auth-page__mobile-logo"
                  />
                  <h2 className="auth-page__mobile-title">Delta Tieup</h2>
               </div>

               <div className="auth-page__form-header">
                  <h2 className="auth-page__form-title">Welcome back</h2>
                  <p className="auth-page__form-subtitle">
                     Sign in with your mobile number to access the dashboard
                  </p>
               </div>

               <FormikProvider value={formik}>
                  <Form className="auth-page__form needs-validation" noValidate onSubmit={handleSubmit}>
                     <CustomInput
                        label="Mobile Number"
                        name="mobileNo"
                        id="login-mobile"
                        inputType="number"
                        maxLength={10}
                        placeholder="E.g 9876541235"
                        {...getFieldProps("mobileNo")}
                        errors={errors.mobileNo && touched.mobileNo}
                        message="Mobile Number Must Be 10 Digits"
                     />

                     <CustomInput
                        label="Password"
                        name="password"
                        id="login-password"
                        inputType="password"
                        placeholder="Enter your password"
                        {...getFieldProps("password")}
                        errors={errors.password && touched.password}
                        message="Password not valid"
                     />

                     <button
                        type="submit"
                        className="auth-page__submit"
                        disabled={loading}
                     >
                        {loading ? (
                           <span className="auth-page__submit-content">
                              <CircularProgress size={22} color="inherit" />
                              Signing in...
                           </span>
                        ) : (
                           "Sign In"
                        )}
                     </button>
                  </Form>
               </FormikProvider>

               <p className="auth-page__footer">
                  Delta Tieup Logistics Platform
               </p>
            </section>
         </div>
      </div>
   );
};

export default Login;
